import { Component, OnInit, ViewChild, Input, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { DoctorInformationService } from './services/doctor-information.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { StateStructure } from '@ptl/state';
import {
  addDoctor,
  removeDoctors,
  addPCP,
  DoctorStateKyruus,
  removePCP,
  updateDoctors,
  updatePCP,
  DOCTOR_STATE_REDUX_ID_KYRUUS
} from './configs/reducers/doctors.reducer';
import { NgRedux } from '@ptl/angular-redux-store';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { UntypedFormBuilder, Form, UntypedFormGroup, AbstractControl } from '@angular/forms';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { Doctor } from './configs/interfaces/doctors.interface';
import { PtlWindowLocationService } from '@ptl/global-components';
import { environment } from 'src/environments/environment';
import { debounceTime, tap } from 'rxjs/operators';
import { LINE_OF_BUSINESS_ENUM } from '@ptl/account-management';
import { ProviderResults } from './configs/interfaces/autocomplete/payload.response';
import { LocationDetails, ProviderDetails } from './configs/interfaces/profile/payload.response';
import { Address, LocationInfo } from './configs/interfaces/shared/provider.interface';
import { ProfileRequestPayload } from './configs/interfaces/profile/payload.request';
import { AutocompleteRequestBody } from './configs/interfaces/autocomplete/payload.request';
import { PlanUtilityService } from '../../plan-selection/plan-summary-list/plan-utility.service';
import { CountyListService } from '@ptl/global-components';
import { CityResponse } from '@ptl/global-components';

export interface DoctorInformationFormAsJson {
  radius: number;
  zipCode: string;
  searchString: string;
}

export interface DoctorInformationFormControls {
  radius: AbstractControl;
  zipCode: AbstractControl;
  searchString: AbstractControl;
}

export interface DoctorInformationFormAsJson {
  radius: number;
  zipCode: string;
  searchString: string;
}

export class HttpError {
  static InternalServerError = 500;
}

@Component({
  selector: 'app-doctor-information-kyruus',
  templateUrl: './doctor-information.component.html',
  styleUrls: ['./doctor-information.component.scss'],
  standalone: false
})
export class DoctorInformationComponent implements OnInit {
  @Input() title = 'Find Doctors';
  @Input() subTitle;
  @Input() description;
  @Input() primaryCareProviderOnly = false;
  @Input() nextButtonLabel: string;
  @Input() nextButtonUrl: string;
  @Input() backButtonLabel: string;
  @Input() backButtonUrl: string;
  @Input() market: "INDIVIDUAL" | "SENIOR";
  marketAsLOBEnum: LINE_OF_BUSINESS_ENUM;
  @Input() defaultRadius: string;

  @Output() doctorsChanged = new EventEmitter<Doctor[]>();

  @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger, static: true })
  inputAutoComplit: MatAutocompleteTrigger;

  disableSubmit: boolean;
  showSpinner: boolean;
  minimumCharError: boolean = false;
  additionalInfoLoading: boolean = false;
  lastLocationQueried: string;

  radius = [
    { id: 5, value: '5 miles' },
    { id: 10, value: '10 miles' },
    { id: 25, value: '25 miles' },
    { id: 50, value: '50 miles' },
    { id: 100, value: '100 miles' }
  ];
  
  autoCompleteResults: Doctor[] = [];
  showFetchMoreSpinner: boolean;
  autocompleteResponse: ProviderResults;
  moreAutocompleteResultsAvailable: boolean;

  form: UntypedFormGroup;
  lastZipCodeQueried: string;
  angularHostUrl: string;

  constructor(
    private fb: UntypedFormBuilder,
    private doctorService: DoctorInformationService,
    private ngRedux: NgRedux<StateStructure>,
    private reducerUtilityService: ReducerUtilityService,
    private windowLocationService: PtlWindowLocationService,
    private planUtilityService: PlanUtilityService,
    private countyListService: CountyListService
  ) {
  }

  get formControls(): DoctorInformationFormControls {
    return this.form as unknown as DoctorInformationFormControls;
  }

  get formValuesAsJson(): DoctorInformationFormAsJson {
    return this.form.value as unknown as DoctorInformationFormAsJson;
  }

  private currentPageNumber: number = 1;

  selectedDoctors: Doctor[] = [];
  showNoResults: Boolean = false;

  ngOnInit() {
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');

    this.form = this.fb.group({
      radius: this.fb.control(this.defaultRadius ? +this.defaultRadius : 10),
      zipCode: this.fb.control(zipCountyState.zipCode),
      searchString: this.fb.control(''),
    });

    this.angularHostUrl = environment.infrastructure.googleCloud.angularHostUrl;
    this.showSpinner = false;
     // This casting to uppercase is to support a scenario where we don't author historically authored pages and their values remain in lowercase from AEM
    this.marketAsLOBEnum = this.market?.toUpperCase() === 'INDIVIDUAL' ? LINE_OF_BUSINESS_ENUM.INDIVIDUAL : LINE_OF_BUSINESS_ENUM.SENIOR;

    // Update title for PCP flow
    if (this.primaryCareProviderOnly) {
      this.title = "Primary Care Provider Information";
    }

    // Broadcast doctors on timeout to update form controls that appear later on the page
    // TODO - implement some sort of 'pageFinishedLoading' event that we can subscribe to after all controls are added
    setTimeout(() => {
      this.doctorsChanged.emit(this.selectedDoctors);
    }, 500);

    const selectedDoctorsState: DoctorStateKyruus = this.reducerUtilityService.getState(DOCTOR_STATE_REDUX_ID_KYRUUS);

    if (this.primaryCareProviderOnly) {
      /** Pre-populate PCP from state during ENROLLMENT flow */
      if (selectedDoctorsState.pcp) {
        this.selectedDoctors.push(selectedDoctorsState.pcp);
      }
    }
    else {
      /** Pre-populated DOCTORS from state during ADD PROVIDER(S) page, DEMOGRAPHICS flow */
      Array.prototype.push.apply(this.selectedDoctors, selectedDoctorsState.doctors);
    }

    this.form.valueChanges.pipe(
      debounceTime(500),
      tap(async (searchValues: DoctorInformationFormAsJson) => {
        if (searchValues.searchString.length > 2) {
          this.minimumCharError = false;
          const providerResults: ProviderResults = await this.filterDoctorsAutoComplete();
        } else {
          this.inputAutoComplit.closePanel();
          this.minimumCharError = true;
        }
      })
    ).subscribe()
  }

  async doctorAdded(addedDoctor: Doctor) {

    this.showSpinner = true;
    this.additionalInfoLoading = true;

    addedDoctor.isSelected = true;

    // call summary API to get main location
    addedDoctor = await this.updateAddedDoctor(addedDoctor);

    if (this.primaryCareProviderOnly) {
      await this.addPCP(addedDoctor);
    } else {
      await this.addDoctor(addedDoctor);
    }

    this.showSpinner = false;
    this.additionalInfoLoading = false;
    this.doctorsChanged.emit(this.selectedDoctors);
  }

  /**
   * Add DOCTOR to selectedDoctors[] to be included as part of DEMOGRAPHICS flow
   * This is our NON-ENROLLMENT, flow
   * @param addedDoctor DOCTOR to be ADDED to SELECTEDDOCTORS[]
   */
  async addDoctor(addedDoctor: Doctor): Promise<void> {
    this.selectedDoctors.push(addedDoctor);
    this.ngRedux.dispatch(addDoctor(addedDoctor));
  }

  /**
   * Set PCP as DOCTOR to be included as part of DEMOGRAPHICS flow
   * This is our NON-ENROLLMENT, flow
   * @param addedDoctor DOCTOR to be SET to PCP
   */
  async addPCP(addedDoctor: Doctor): Promise<void> {

    // reset replaced doctor's selected flag back to false. if it's still in autocomplete results it will still be shown as 'added'
    // because autoCompleteResults can reset all references
    this.autoCompleteResults
      .filter((result) => addedDoctor.providerId !== result.providerId)
      .forEach((doc) => {
        doc.isSelected = false;
      });
    
    // wipe out existing selected list of doctors
    this.selectedDoctors = [];
    // add the doctor to the list of doctors
    this.selectedDoctors.push(addedDoctor);
    
    this.ngRedux.dispatch(addPCP(addedDoctor));
  }

  async updateAddedDoctor(doctor: Doctor): Promise<Doctor> {
    try {

      doctor.fivePartKey_providerId = this.doctorService.getClientLocationIdAttribute(doctor.kyruusProvider).value;

      if (doctor.locationsCount > 1) {
        const doctorDetailsRequest: ProfileRequestPayload = {
          languageCode: "EN",
          location: this.lastZipCodeQueried,
          locationId: doctor.kyruusProvider.location.id,
          productCode: "all",
          providerId: doctor.kyruusProvider.id
        }
      
        if (this.primaryCareProviderOnly) {
          const plan = this.planUtilityService.getSelectedPlan();
          const productCode = plan.attributes.PRODUCT_CODE;
          doctorDetailsRequest.productCode = productCode;        
        }
        const doctorDetails: ProviderDetails = await this.doctorService.profileSearch(this.marketAsLOBEnum, doctorDetailsRequest);
        doctor = this.getOtherLocations(doctor, doctorDetails);
      }

    }
    catch (e) {
      console.error('Failed to retrieve Provider summary for ', doctor);
    } finally {
      this.inputAutoComplit.closePanel();
    }

    return doctor;
  }

  async updateLocation(doctor: Doctor, location: LocationInfo): Promise<void> {
    doctor.locationId = location.id.toString();
    doctor.address = this.getStringedAddress(location.address); // let's update top-level object with selected location
    doctor.kyruusProvider.location = location; // let's update current location ON the kyruus response object 
    
    doctor.fivePartKey_providerId = this.doctorService.getClientLocationIdAttribute(doctor.kyruusProvider.location as LocationDetails).value; // let's use the newly set location to confirm 5-part-key, though should be identical to the provider

    if (this.primaryCareProviderOnly) {
      this.ngRedux.dispatch(updatePCP(doctor));
    }
    else {
      this.ngRedux.dispatch(updateDoctors(doctor));
    }
  }

  /**
   * remove doc from selectedDoctors[] to be removed as an item in the demographics flow
   * @param doc doctor to be removed from selectedDoctors[]
   */
  removeDoctor(doc: Doctor): void {
    // remove the doctor from list of selected ones
    const doctorIndex = this.selectedDoctors.indexOf(doc);
    this.selectedDoctors.splice(doctorIndex, 1);

    // reset selected object back to false. if it's still in autocomplete results it will reactivate
    // we can't just use the 'doc' argument because autoCompleteResults can reset all references
    const docResult = this.autoCompleteResults.filter((result) => doc.providerId === result.providerId);
    if (docResult[0]) {
      docResult[0].isSelected = false;
    }

    this.ngRedux.dispatch(removeDoctors(doc));
    this.ngRedux.dispatch(removePCP(doc));
    this.doctorsChanged.emit(this.selectedDoctors);
  }

  async fetchMoreAutocompleteResults(event: Event): Promise<void> {
    
    // Autocomplete clears itself otherwise on sensing a click
    event.stopPropagation();

    this.showFetchMoreSpinner = true;

    try {
      await this.filterDoctorsAutoComplete(++this.currentPageNumber);
    } catch (e) {
      console.error(e);
    } finally {
      this.showFetchMoreSpinner = false;
    }

  }

  async filterDoctorsAutoComplete(pageNumber: number = 1, customSearch?: string): Promise<ProviderResults> {

    let autocompleteResponse: ProviderResults = null;

    this.showNoResults = false;
    this.disableSubmit = true;

    // Results of THIS query
    const scopedQueryResults: Doctor[] = []; 

    if (pageNumber === 1) {
      this.showSpinner = true;

      // Results of EXISTING RESULTS  + THIS query
      this.autoCompleteResults = [];
    } else {
      this.showSpinner = true;
    }

    const formValues: DoctorInformationFormAsJson = this.formValuesAsJson;
    
    try {
      if (this.lastZipCodeQueried !== formValues.zipCode) {
        this.lastZipCodeQueried = formValues.zipCode;

        const cityResponse: CityResponse = await this.countyListService.getCityInfoByZip(this.lastZipCodeQueried);
        const city = cityResponse.cityData && cityResponse.cityData[0];
        
        this.lastLocationQueried = `${city.name}, ${city.stateAbbreviation}, ${city.zipCode5}`
      }
      
      let kyruusConfigs: AutocompleteRequestBody = {
        languageCode: "EN",
        location: this.lastLocationQueried,
        page: pageNumber, // TODO - more results
        pageSize: 200,
        productCode: "all",
        query: formValues.searchString,
        radius: formValues.radius,
        searchCategory: "GENERAL",
        sort: "NAME_ASC"
      };
      
      if (this.primaryCareProviderOnly) {
        const plan = this.planUtilityService.getSelectedPlan();
        const productCode = plan.attributes.PRODUCT_CODE;
        kyruusConfigs.productCode = productCode;        
        kyruusConfigs.filters = ["ELIGIBLE_PCP:YES"];
      }

      autocompleteResponse = await this.doctorService.autocompleteSearch({
        lob: this.marketAsLOBEnum,
        kyruusConfigs
      });

      const arrayDoctors = autocompleteResponse.providerResults;

      for (const doctor of arrayDoctors) {

        /** Only add, to auto-complete results, doctors who haven't been added */
        const isDoctorAlreadySelected
          = this.selectedDoctors.filter(doc => {
            return doc.id === doctor.id;
          }).length > 0;

        scopedQueryResults.push({
          id: doctor.id,
          title: this.doctorService.getSpecialtyAttribute(doctor).value.join('<br/>'), // doctor.specialty,
          name: doctor.provider.fullName, // doctor.name,
          address:this.getStringedAddress(doctor.location.address),
          providerId: doctor.id.toString(),
          locationId: doctor.location.id.toString(),
          locationsCount: doctor.searchRelevantLocationCount,
          isSelected: isDoctorAlreadySelected,
          kyruusProvider: doctor // Store autocomplete response here, it will be out primary means of interacting with the new data
        });
      }

      this.autoCompleteResults = this.autoCompleteResults.concat(scopedQueryResults);
      
      // Determine if we can get MORE results
      this.moreAutocompleteResultsAvailable = this.autoCompleteResults.length < autocompleteResponse.totalNumberOfResults;

      this.inputAutoComplit.openPanel();

      // After searching once, we can show the no results message (if applicable)
      this.showNoResults = true;
    } catch (e) { 
      console.log(e);
    } finally {  
      this.showSpinner = false;
      this.disableSubmit = false;
    }

    return autocompleteResponse;

  }

  /** Returns address as a single string */
  getStringedAddress(addressObj: Address): string {
    let address: string = null;
    if (addressObj.line2) {
      address =
        `${addressObj.line1}, ` +
        `${addressObj.line2}, ` +
        `${addressObj.city}, ` +
        `${addressObj.state}`;
    } else {
      address =
        `${addressObj.line1}, ` +
        `${addressObj.city}, ` +
        `${addressObj.state}`;
    }
    return address;
  }
  getOtherLocations(doctor: Doctor, providerDetails: ProviderDetails): Doctor {
    try {

      let otherLocations = [];

      /** Add data of other locations */
      for (const docAtOtherLocation of providerDetails.locations) {

        /** Only add, to otherLocation results (doctor with locations), with different locationId's */
        const selectedDoctorAtLocationAlreadyExists
          = this.selectedDoctors.filter(doc => {
            return doc.locationId.toString() === docAtOtherLocation.id.toString();
          }).length > 0;

        if (!selectedDoctorAtLocationAlreadyExists) {

          otherLocations.push({
            displayDetails: {
              // id: docAtOtherLocation.id,
              title: this.doctorService.getSpecialtyAttribute(docAtOtherLocation)?.value.join('<br/>'),
              name: doctor.name,
              address: this.getStringedAddress(docAtOtherLocation.address),
              providerId: doctor.id,
              locationId: docAtOtherLocation.id
            },
            ...docAtOtherLocation
          });
          console.log(otherLocations);
        }
      }
      
      doctor.otherLocations = otherLocations;

    } catch (e) {
      console.error('Failed to get other locations', e, doctor);
    }

    return doctor;
  }

  navBack() {
    if (this.backButtonUrl) {
      // window.location.href = `./${this.backButtonUrl}`;
      const finalUrl = `${this.backButtonUrl}`;
      this.windowLocationService.goToUrl(finalUrl);
    } else {
      console.error('Missing AEM url for {backButtonUrl}');
    }
  }

  navForward() {
    if (this.nextButtonUrl) {
      // window.location.href = `./${this.nextButtonUrl}`;
      const finalUrl = `${this.nextButtonUrl}`;
      this.windowLocationService.goToUrl(finalUrl);
    } else {
      console.error('Missing AEM url for {nextButtonUrl}');
    }
  }
  trackByLocationId(index: number, locationId: string) {
    return locationId;
  }
}
